import { useEffect, useState } from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { TrendingUp, TrendingDown } from "lucide-react";
import { api } from "@/lib/api";
import { useNetWorth, formatINR, tinyINR } from "@/context/NetWorthContext";
import { getLang, t } from "@/lib/i18n";

const fmtDate = (iso) => new Date(iso).toLocaleDateString(getLang() === "hi" ? "hi-IN" : undefined, { day: "numeric", month: "short", year: "numeric" });
const fmtTime = (iso) => new Date(iso).toLocaleString(getLang() === "hi" ? "hi-IN" : undefined, { dateStyle: "medium", timeStyle: "short" });

const Tip = ({ active, payload }) => (active && payload?.length ? (
  <div className="rounded-lg bg-[#1C2421] text-[#FDFBF7] px-2.5 py-1.5 text-xs shadow-lg">
    <p className="font-mono text-[#E2BE52]">{formatINR(payload[0].value)}</p>
    <p className="opacity-70">{fmtTime(payload[0].payload.at)}</p>
  </div>
) : null);

export const NetWorthChart = ({ personId }) => {
  const { map } = useNetWorth();
  const [pts, setPts] = useState(null);
  const stamp = map[personId]?.updated_at;
  useEffect(() => {
    api.get(`/persons/${personId}/networth/history`).then(({ data }) => setPts(data)).catch(() => setPts([]));
  }, [personId, stamp]);
  if (!pts?.length) return null;
  const first = pts[0].amount;
  const last = pts[pts.length - 1].amount;
  const diff = last - first;
  const Trend = diff >= 0 ? TrendingUp : TrendingDown;
  return (
    <div className="mt-3 rounded-2xl bg-[#F4EDE2]/60 p-3" data-testid={`networth-chart-${personId}`}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-mono uppercase tracking-[0.15em] text-[#7E8E85]">{t("Net worth over time")}</span>
        {pts.length > 1 && (
          <span className={`flex items-center gap-1 font-semibold ${diff >= 0 ? "text-[#2E7D32]" : "text-[#C62828]"}`} data-testid="networth-chart-change">
            <Trend className="w-3.5 h-3.5" />{diff >= 0 ? "+" : "−"}₹{tinyINR(Math.abs(diff))}
          </span>
        )}
      </div>
      {pts.length === 1 ? (
        <p className="text-xs text-[#7E8E85]" data-testid="networth-chart-single">{t("First entry on")} {fmtDate(pts[0].at)}. {t("The chart grows with each update.")}</p>
      ) : (
        <div className="h-28">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={pts} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id={`nwg-${personId}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C59B27" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#C59B27" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="at" interval="preserveStartEnd" tickFormatter={fmtDate} tick={{ fontSize: 10, fill: "#7E8E85" }} axisLine={false} tickLine={false} minTickGap={24} />
              <YAxis tickCount={3} domain={[(m) => Math.max(0, m - (m * 0.1 || 1)), (m) => m + (m * 0.1 || 1)]} tickFormatter={(v) => tinyINR(v)} tick={{ fontSize: 10, fill: "#7E8E85" }} axisLine={false} tickLine={false} width={48} />
              <Tooltip content={<Tip />} />
              <Area type="monotone" dataKey="amount" stroke="#C59B27" strokeWidth={2} fill={`url(#nwg-${personId})`} dot={{ r: 3, fill: "#1E3A2B" }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};
